import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

/**
 * Events — the calendar the public site reads. Create, edit, publish and
 * retire gatherings; sourceUrl keeps every event anchored to where it was
 * announced, in keeping with the platform's sourcing rule.
 */
const EMPTY = {
  title: '', description: '', date: '', endDate: '', location: '',
  sector: 'delhi', type: 'Observance', imageUrl: '', sourceUrl: '',
  seatsTotal: '', published: true,
};

const TYPES = ['Observance', 'DMS', 'Retreat', 'Samgiita', 'Seminar', 'Service Camp'];

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await api.get('/events?all=1');
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch events', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditId(null); setFormData(EMPTY); setShowModal(true); };
  const openEdit = (ev) => {
    setEditId(ev._id);
    setFormData({ ...EMPTY, ...ev, seatsTotal: ev.seatsTotal ?? '' });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.date) {
      alert('Title and date are required.');
      return;
    }
    const payload = {
      ...formData,
      seatsTotal: formData.seatsTotal === '' ? null : Number(formData.seatsTotal),
      seatsLeft: formData.seatsTotal === '' ? null : Number(formData.seatsTotal),
    };
    try {
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post('/events', payload);
      setShowModal(false);
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}" (${ev.date})? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${ev._id}`);
      fetchEvents();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const togglePublish = async (ev) => {
    try {
      await api.put(`/events/${ev._id}`, { published: !ev.published });
      fetchEvents();
    } catch (err) {
      alert('Update failed');
    }
  };

  const field = 'w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2A2858]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Events</h1>
          <p className="text-sm opacity-70">The calendar the public site reads — every event carries its source.</p>
        </div>
        <button type="button" onClick={openCreate} className="bg-[#2A2858] text-white font-bold px-5 py-3 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
          + New Event
        </button>
      </div>

      {loading ? (
        <p className="opacity-70">Loading events…</p>
      ) : events.length === 0 ? (
        <div className="rounded-xl border border-stone-200 p-8 text-center opacity-80">
          No events yet. Run <code>node scripts/seed-events.js</code> in the engine to import the five curated 2026 gatherings, or create one above.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="p-3">Date</th>
                <th className="p-3">Title</th>
                <th className="p-3">Type</th>
                <th className="p-3">Location</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev._id} className="border-b border-stone-100 last:border-0">
                  <td className="p-3 whitespace-nowrap">{ev.date}{ev.endDate ? ` → ${ev.endDate}` : ''}</td>
                  <td className="p-3 font-medium">{ev.title}</td>
                  <td className="p-3">{ev.type}</td>
                  <td className="p-3">{ev.location}</td>
                  <td className="p-3">
                    <button type="button" onClick={() => togglePublish(ev)}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${ev.published ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                      {ev.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => openEdit(ev)} className="px-3 py-1 rounded-lg text-xs font-bold bg-stone-100 hover:bg-stone-200 mr-2">Edit</button>
                    <button type="button" onClick={() => handleDelete(ev)} className="px-3 py-1 rounded-lg text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editId ? 'Edit Event' : 'New Event'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <input name="title" value={formData.title} onChange={handleChange} placeholder="Title *" className={field} />
              <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" rows={3} className={field} />
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-sm">Start date *
                  <input type="date" name="date" value={formData.date} onChange={handleChange} className={field} />
                </label>
                <label className="block text-sm">End date
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className={field} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input name="location" value={formData.location} onChange={handleChange} placeholder="Location" className={field} />
                <select name="type" value={formData.type} onChange={handleChange} className={field}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <input name="sourceUrl" value={formData.sourceUrl} onChange={handleChange} placeholder="Source URL — where the event is announced" className={field} />
              <div className="grid grid-cols-2 gap-4">
                <input type="number" name="seatsTotal" value={formData.seatsTotal} onChange={handleChange} placeholder="Seats (blank = open registration)" className={field} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" name="published" checked={formData.published} onChange={handleChange} />
                  Published
                </label>
              </div>
              <div>
                <p className="text-sm mb-2">Poster image</p>
                <ImageUploader onUploadSuccess={(url) => setFormData((f) => ({ ...f, imageUrl: url }))} />
                <input name="imageUrl" value={formData.imageUrl} onChange={handleChange} placeholder="…or an image path, e.g. /event-poster.webp" className={`${field} mt-2`} />
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-stone-100 text-stone-500 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
                <button type="submit" className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
                  {editId ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
